'use client'

import { useRef } from 'react'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  FormilySchemaRenderer,
  type IFormilyRendererRef,
} from '@/components/formily/renderer'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'
import { useTranslations } from 'next-intl'
import type { ISuperPromptModel } from '@/lib/db/types'
import { popModal } from '@/components/modals'

export const SuperPromptShowContainer = ({
  superPrompt,
  defaultValues,
  onSubmit,
  onDelete,
}: {
  superPrompt: ISuperPromptModel
  defaultValues?: Record<string, any>
  onSubmit?: (data: FormData) => void
  onDelete?: () => void
}) => {
  const formRef = useRef<IFormilyRendererRef>(null)

  const { dt } = useDynamicI18n()
  const t = useTranslations('apps-feature')

  const submitHandler = () => {
    if (!formRef.current) return
    const form = formRef.current?.form

    form
      .submit()
      .then((values) => {
        onSubmit?.(values as FormData)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  // 该组件是从 app copy 过来的
  const app = superPrompt

  return (
    <div className="flex h-full w-full flex-col md:flex-row overflow-hidden bg-[#212121] relative">
      {/* Close button */}
      <button
        onClick={() => popModal()}
        className="absolute top-4 right-4 z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none text-white hover:bg-white/10 p-2"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      {/* Left section with white overlay */}
      <div className="flex-1 bg-white/[0.06] flex flex-col overflow-hidden">
        <div className="px-5 py-8 flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 flex-shrink-0">
            <div className="flex items-center justify-center gap-2">
              {app.logo ? (
                <img
                  src={app.logo}
                  alt={app.name}
                  className="h-10 text-white rounded-md p-1"
                />
              ) : (
                <div className="h-8 w-12 text-white rounded-md p-1 bg-gray-800 flex items-center justify-center text-xs">
                  Logo
                </div>
              )}
              <span className="text-white text-xl font-semibold">
                {app.name}
              </span>
            </div>
          </div>

          <p className="text-white text-base mb-6 font-normal leading-relaxed flex-shrink-0">
            {dt(app.description)}
          </p>

          {/* Preview area */}
          <div className="w-full flex-1 flex flex-col justify-start min-h-0">
            {app.example ? (
              <div className="flex flex-col h-full">
                <div className="flex-1 flex items-start justify-start min-h-0">
                  <img
                    src={app.example}
                    alt={app.name}
                    className="w-full h-auto max-h-full object-cover object-left rounded-lg"
                  />
                </div>
                <p className="text-gray-500 text-lg text-left italic w-full mt-3 flex-shrink-0">
                  {t('just_a_sample_image')}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Right section - Form */}
      <div className="w-full md:w-2/5 border-t md:border-t-0 md:border-l border-gray-800 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto px-5 py-8">
          <FormilySchemaRenderer
            defaultValues={defaultValues}
            formilySchema={app?.parameters?.prompt}
            needTranslation={true}
            ref={formRef}
          />
        </div>
        <div className="px-5 py-6 flex justify-end border-t border-gray-800 flex-shrink-0">
          <div className="flex gap-3 min-w-0 flex-shrink-0">
            {defaultValues ? (
              <>
                <button
                  onClick={onDelete}
                  className="px-4 py-3 bg-transparent border border-white/20 hover:bg-white/10 text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  {t('delete')}
                </button>
                <button
                  onClick={submitHandler}
                  className="px-4 py-3 bg-white hover:bg-white/90 text-black rounded-lg transition-colors font-medium whitespace-nowrap"
                >
                  {t('save')}
                </button>
              </>
            ) : (
              <button
                onClick={submitHandler}
                className="px-4 py-3 bg-white hover:bg-white/90 text-black rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                {t('add_to_project')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const Container = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn(
        'relative rounded-xl overflow-hidden',
        'bg-[#212121]',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const SuperPromptModal = ({
  superPrompt,
  defaultValues,
  debug = false,
  onSubmit,
  onDelete,
}: {
  superPrompt: ISuperPromptModel
  defaultValues?: Record<string, any>
  debug?: boolean
  onSubmit?: (data: FormData) => void
  onDelete?: () => void
}) => {
  const t = useTranslations('apps-feature.modal')

  const onSubmitHandler = async (data: FormData) => {
    onSubmit?.(data)
    if (debug) {
      return
    }
  }

  return (
    <DialogContent
      className="w-[60vw] h-[60vh] max-h-[560px] max-w-none p-0 bg-transparent border-0"
      onInteractOutside={(e) => {
        e.preventDefault()
        popModal()
      }}
      onEscapeKeyDown={() => popModal()}
    >
      <Container>
        <DialogHeader hidden>
          <DialogTitle hidden>{t('title')}</DialogTitle>
        </DialogHeader>
        <SuperPromptShowContainer
          superPrompt={superPrompt}
          defaultValues={defaultValues}
          onSubmit={onSubmitHandler}
          onDelete={onDelete}
        />
      </Container>
    </DialogContent>
  )
}
